import {observe} from '@github-ui/selector-observer'

const hiddenTimePeriodOnScrollInMs = 1000

// Maps each snippet container to its copy button. Weakly held so removed snippets can be
// garbage collected without any explicit cleanup.
const clipboardCopyElements = new WeakMap<Node, HTMLElement>()

const snippetClipboardCopyTemplate = document.querySelector<HTMLTemplateElement>('#snippet-clipboard-copy-button')
const snippetClipboardCopyTemplateUnpositioned = document.querySelector<HTMLTemplateElement>(
  '#snippet-clipboard-copy-button-unpositioned',
)

function insertSnippetClipboardCopyButton(el: HTMLElement) {
  const clipboardContent = el.getAttribute('data-snippet-clipboard-copy-content')
  if (clipboardContent === null) return

  // Remove this attribute so that we don't add a second button.
  el.removeAttribute('data-snippet-clipboard-copy-content')

  const useUnpositioned = !!el.closest('.js-snippet-clipboard-copy-unpositioned')
  const template = useUnpositioned ? snippetClipboardCopyTemplateUnpositioned : snippetClipboardCopyTemplate

  if (!(template instanceof HTMLTemplateElement)) return

  const documentFragment = template.content.cloneNode(true) as DocumentFragment

  const clonedZeroClipboardContainer = documentFragment.children[0]
  if (!(clonedZeroClipboardContainer instanceof HTMLElement)) return

  const clipboardCopyElement = clonedZeroClipboardContainer.children[0]
  if (!(clipboardCopyElement instanceof HTMLElement)) return

  clipboardCopyElement.setAttribute('value', clipboardContent)

  if (!useUnpositioned) {
    clipboardCopyElements.set(el, clipboardCopyElement)

    const scrollableElement = el.querySelector('pre')
    if (scrollableElement !== null) {
      let timer: ReturnType<typeof setTimeout> | undefined

      /* eslint-disable-next-line github/prefer-observers */
      scrollableElement.addEventListener('scroll', () => {
        if (timer) clearTimeout(timer)

        clipboardCopyElement.style.display = 'none'
        timer = setTimeout(() => {
          clipboardCopyElement.style.display = 'inherit'
        }, hiddenTimePeriodOnScrollInMs)
      })
    }
  }

  el.appendChild(clonedZeroClipboardContainer)
}

// A single document-level listener, rather than one per snippet, so listeners never outlive
// the snippets they reference across soft navigations.
document.addEventListener('selectionchange', () => {
  const selection = document.getSelection()
  if (!selection) return

  for (let node = selection.anchorNode; node; node = node.parentNode) {
    const clipboardCopyElement = clipboardCopyElements.get(node)
    if (clipboardCopyElement) {
      clipboardCopyElement.style.display = selection.toString().trim() === '' ? 'inherit' : 'none'
      return
    }
  }
})

observe('[data-snippet-clipboard-copy-content]', {
  constructor: HTMLElement,
  add(el) {
    insertSnippetClipboardCopyButton(el)
  },
})
